/**
 * TanStack Router search stringifier that preserves repeated keys.
 *
 * The default stringifier collapses array values into a single JSON-encoded
 * param (its qss encoder is documented as "single value per key"). Better
 * Auth's signed OAuth authorize query carries repeated `ba_param` keys, and
 * collapsing them corrupts the signature. This encoder emits array values as
 * repeated keys and matches the default scalar encoding, so the signed query
 * survives the router intact and Better Auth's own client plugin can read it
 * straight from `window.location.search`. Pass as `createRouter({ stringifySearch })`.
 */
export function stringifySearch(search: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const key in search) {
    const value = search[key];
    if (value === undefined) continue;
    const list = Array.isArray(value) ? value : [value];
    for (const item of list) params.append(key, encodeSearchValue(item));
  }
  const str = params.toString();
  return str ? `?${str}` : "";
}

/** Matches TanStack's default value encoding (JSON for objects and JSON-parseable strings). */
function encodeSearchValue(value: unknown): string {
  if (typeof value === "object" && value !== null) {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  if (typeof value === "string") {
    try {
      JSON.parse(value);
      return JSON.stringify(value);
    } catch {
      return value;
    }
  }
  return String(value);
}
